<template>
  <v-container fluid pa-0>
    <helpers-section-sub-header>Periodic Rebalancing</helpers-section-sub-header>
    <p class="text-body-2 mb-2">
      Periodically re-evaluates all open positions against your configured entry rules and
      adjusts sizes to stay in proportion. Useful for keeping the portfolio aligned with current
      signal strength rather than letting original buy sizes drift as the portfolio grows.
      Choose how frequently the rebalance runs — every 20 trading days (~monthly), 60 (~quarterly),
      126 (~semi-annually), or 252 (~annually).
    </p>
    <v-row>
      <v-col cols="12" sm="6" md="2" xl="2">
        <v-checkbox
          v-model="criteria.rebalancing.enabled"
          hide-details
          density="compact"
          label="Enabled"
          class="shrink mr-2"
        />
      </v-col>
      <v-col v-if="1 == 2" cols="12" sm="6" md="4" xl="3">
        <v-checkbox
          v-model="criteria.rebalancing.f13_timing"
          hide-details
          density="compact"
          :disabled="!criteria.rebalancing.enabled"
          label="Rebalance when 13F data is updated quarterly"
          class="shrink mr-2"
        />
      </v-col>
      <v-col cols="12" sm="6" md="4" xl="3">
        <v-select
          v-model="criteria.rebalancing.frequency"
          :items="rebalancing_frequencies"
          :disabled="!criteria.rebalancing.enabled"
          item-title="text"
          label="Rebalancing Frequency"
          class="shrink"
          density="compact"
          variant="outlined"
          hide-details
        />
      </v-col>
    </v-row>
    <!-- Individual Position -->
    <helpers-section-sub-header class="mt-6">Individual Position Limits</helpers-section-sub-header>
    <p class="text-body-2 mb-2">
      Cap the maximum portfolio allocation for any single stock. If a position grows beyond the
      limit due to price appreciation or signal-triggered additions, choose whether to trim it
      back or simply block further buys while the cap is exceeded.
    </p>
    <p class="text-body-2 mb-2">
      <strong>Max % Above Initial Size</strong> allows a position to drift above its initial entry
      size by up to this percentage before the cap is enforced — letting winners run slightly
      before trimming kicks in. Leave blank to enforce the cap strictly from the moment of entry.
    </p>
    <v-row>
      <v-col cols="12" sm="6" lg="5" xl="4">
        <v-text-field
          v-model="criteria.max_individual"
          label="Max % of portfolio per position"
          suffix="%"
          density="compact"
          variant="outlined"
          hint="Leave blank to impose no per-position cap"
          persistent-hint
        />
      </v-col>

      <v-col cols="12" sm="6" lg="5" xl="4">
        <v-text-field
          v-model="criteria.max_individual_above_initial"
          label="Allow position to grow this % above initial size before capping"
          suffix="%"
          density="compact"
          variant="outlined"
          hint="Leave blank to enforce the cap strictly at entry size"
          persistent-hint
        />
      </v-col>

      <v-col cols="12">
        <v-radio-group v-model="criteria.trim_position_if_over_max">
          <v-radio
            :value="1"
            label="Trim position back down to the maximum allocation"
          />
          <v-radio
            :value="2"
            label="Keep existing position unchanged, but block any further additions"
          />
        </v-radio-group>
      </v-col>
    </v-row>

    <!-- Long vs Short Allocation -->
    <helpers-section-sub-header v-if="1 == 2">
      Long vs Short Allocation:
    </helpers-section-sub-header>
    <v-row v-if="1 == 2">
      <v-col cols="12" sm="6" md="4" xl="3">
        <v-text-field
          v-model="criteria.max_long"
          label="Max % Allocated to Longs"
          suffix="%"
          hide-details
          density="compact"
          variant="outlined"
          class="mt-0"
        />
      </v-col>
      <v-col cols="12" sm="6" md="4" xl="3">
        <v-text-field
          v-model="criteria.max_short"
          label="Max % Allocated to Shorts"
          suffix="%"
          density="compact"
          variant="outlined"
          hide-details
          class="mt-0"
        />
      </v-col>
    </v-row>

    <!-- Sector/Industry Allocations -->
    <helpers-section-sub-header class="mt-6">Sector / Industry Concentration Limits</helpers-section-sub-header>
    <p class="text-body-2 mb-2">
      Prevent over-concentration in a single sector or industry. When the combined value of
      positions in a sector or industry would exceed the cap, new buys in that group are blocked
      until existing positions shrink back below the limit. Leave blank to impose no limit.
      For example, a 20% industry cap and 35% sector cap force diversification even when signals
      cluster heavily in one area.
    </p>
    <v-row>
      <v-col cols="12" sm="6" md="4" xl="3">
        <v-text-field
          v-model="criteria.max_industry"
          label="Max % allocated to any single industry"
          suffix="%"
          density="compact"
          variant="outlined"
          hint="Leave blank for no industry concentration limit"
          persistent-hint
          class="mt-0"
        />
      </v-col>
      <v-col cols="12" sm="6" md="4" xl="3">
        <v-text-field
          v-model="criteria.max_sector"
          label="Max % allocated to any single sector"
          suffix="%"
          density="compact"
          variant="outlined"
          hint="Leave blank for no sector concentration limit"
          persistent-hint
          class="mt-0"
        />
      </v-col>
    </v-row>

    <!-- Marketcap Allocations -->
    <helpers-section-sub-header class="mt-6">Market Cap Concentration Limit</helpers-section-sub-header>
    <p class="text-body-2 mb-2">
      Cap the total allocation to any single market cap tier (e.g., micro, small, mid, large).
      Prevents a strategy from becoming purely a small-cap or large-cap portfolio when signals
      concentrate in one tier. Leave blank to impose no limit.
    </p>
    <v-row>
      <v-col cols="12" sm="6" md="4" xl="3">
        <v-text-field
          v-model="criteria.max_market_cap"
          label="Max % allocated to any single market cap tier"
          suffix="%"
          density="compact"
          variant="outlined"
          hint="Leave blank for no market cap concentration limit"
          persistent-hint
          class="mt-0"
        />
      </v-col>
    </v-row>

    <!-- Limit Exposure for each Enabled Signal Source -->
    <helpers-section-sub-header class="mt-6 mb-2">Limit Exposure per Signal Source</helpers-section-sub-header>
    <p class="text-body-2 mb-4">
      Set a maximum total portfolio allocation for positions generated by each signal type.
      When a signal's combined position value would exceed its cap, new buys from that signal
      are blocked until existing positions from it shrink below the limit. This lets you blend
      multiple signal types without one dominating the portfolio. Leave blank for no limit on
      that signal.
    </p>
    <v-row>
      <v-col cols="12" sm="4" xl="3">
        <div v-for="signal in criteria.signals" :key="signal.signal_type" class="mb-6">
          <h4 class="mb-1">{{ signalLabel(signal.signal_id) }}</h4>
          <v-text-field
            v-model="signal.max_risk"
            label="Max % of portfolio"
            type="number"
            min="0"
            density="compact"
            variant="outlined"
            suffix="%"
            hint="Leave blank for no limit"
            persistent-hint
          />
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
// Modules

import { mapWritableState } from 'pinia'
import { useUserStore, useAppStore, useWwStore } from '@/store'
export default {
  data() {
    return {
      rebalancing_frequencies: [
        { text: 'Every 20 Trading Days', value: 1 },
        { text: 'Every 60 Trading Days', value: 2 },
        { text: 'Every 126 Trading Days', value: 3 },
        { text: 'Every 252 Trading Days', value: 4 }
      ]
    }
  },
  computed: {
    ...mapWritableState(useAppStore, ['whaleTraderCriteria']),
    /*...mapFields({
      appcriteria: 'app.whaleTraderCriteria',
     // criteria: 'app.whaleTraderCriteria.risk_management'
    }),*/
    criteria() {
      return this.whaleTraderCriteria.risk_management
    },
    appcriteria() {
      return this.whaleTraderCriteria
    }
  },
  methods: {
    signalLabel(id) {
      switch (id) {
        case 1:
          return '13F Signals'
        case 2:
          return '13D/G Signals'
        case 3:
          return 'Insider Signals'
        case 4:
          return 'Trend Signals'
        case 6:
          return 'WhaleIndex Signals'
        case 7:
          return 'Fundamental Signals'
      }
    }
  }
}
</script>
